import React from 'react';
import { useTranslation } from "react-i18next";
import ReportPage from '../ReportChart/ReportPage';
import { Apis } from 'services/api';
import API from "helpers/api";
import { getAPIResponse } from 'helpers/getApiResponse';
import { useChartColors } from '../ReportChart/hooks';

// Helper function to build department IDs query string
const buildDepartmentQuery = (siteDepartmentId = "", siteDepartmentIds = []) => {
	if (siteDepartmentIds && siteDepartmentIds.length > 0) {
		const query = siteDepartmentIds.map(id => `siteDepartmentIds=${id}`).join('&');
		return query ? `&${query}` : "";
	} else if (siteDepartmentId) {
		return `&siteDepartmentId=${siteDepartmentId ?? ""}`;
	}
	return "";
};

const getAverageTimes = async ({
	modelVersionId = "",
	siteAssetRegisterId = "",
	modelVersionRoleId = "",
	modelVersionIntervalId = "",
	modelVersionStageId = "",
	modelVersionZoneId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
	roleId = "",
	occurrence = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/averagetimes?modelVersionId=${modelVersionId}&siteAssetRegisterId=${siteAssetRegisterId}&startDate=${startDate}&endDate=${endDate}${modelVersionRoleId ? `&modelVersionRoleId=${modelVersionRoleId}` : ''}&modelVersionIntervalId=${modelVersionIntervalId}&modelVersionStageId=${modelVersionStageId}&modelVersionZoneId=${modelVersionZoneId}${deptQuery}&modelId=${modelId}&roleId=${roleId}&occurrence=${occurrence}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const ServiceAverageTimesReport = () => {
  const { customCaptions } = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const { getColorsByCategory, getAllColors } = useChartColors();
  const { t } = useTranslation();

  const chartConfig = {
    id: 'service-average-times',
    title: t("title.entityAverageTimes", { entity: customCaptions?.service }),
    drilldownLabel: (dataPoint) => dataPoint 
      ? `${dataPoint?.payload?.label}`
      : `${customCaptions.versionPlural || 'Versions'}`,
    fetchData: async (params) => {
      // Return invalid filters status if modelId or roleId is not set
      if (!params.modelId || !params.roleId) {
        return { 
          status: false, 
          invalidFilters: true,
          message: t("message.pleaseSelectModelAndRole", { model: customCaptions?.model || 'Model', role: customCaptions?.role || 'Role' })
        };
      }

      // Map roleId to modelVersionRoleId because that's really what it is
      params['modelVersionRoleId'] = params.roleId;

      // We display all versions so strip out the currently selected version at this level
      delete params.modelVersionId;

      // Clean up params - remove empty occurrence values
      if (params.occurrence === '' || params.occurrence === undefined) {
        delete params.occurrence;
      }

      return await getAverageTimes(params);
    },
    allowedChartTypes: ['vertical-bar', 'horizontal-bar'],
    defaultChartType: 'vertical-bar', 
    axisConfig: { 
      categoryAxis: {
        dataKey: 'label',
        type: 'category',
      },
      valueAxes: [
        {
          label: t("label.minutes"),
        }
      ]
    },
    series: [
      {
        key: 'estimatedMinutes',
        label: t("label.estimatedTimeMinutes"),
        type: 'bar',
        colors: [getColorsByCategory('primary')[0]]
      },
      {
        key: 'actualMinutes',
        label: t("label.actualTimeMinutes"),
        type: 'bar',
        colors: [getColorsByCategory('primary')[1]],
        stackId: 'stack1'
      },
      {
        key: 'pauseMinutes',
        label: t("label.pauseTimeMinutes"),
        type: 'bar', 
        colors: [getColorsByCategory('neutral')[0]],
        stackId: 'stack1',
        drilldown: false
      }
    ],
    transformData: (response, filterValues) => {
      if (!response?.data?.data) return null;

      const modelInfo = filterValues.model.value;

      return response.data.data.map(item => ({
        id: item.id,
        label: `${modelInfo.name} v${item.version}`,
        estimatedMinutes: parseFloat((item.estimatedMinutes).toFixed(2)),
        actualMinutes: parseFloat((item.actualMinutes).toFixed(2)),
        pauseMinutes: parseFloat((item.pauseMinutes || 0).toFixed(2)),
        depth: response.data.depth
      }));
    },
    filters: {
      timeframe: {
        allowCustomRange: true,
        defaultRange: 'show-all',
      },
      department: { multiSelect: true },
      model: {
        required: true,
        showAll: false,
      },
      role: { 
        required: true,
        showAll: false
      },
      asset: { 
        show: model => model?.modelTemplateType === 'A'
      },
      interval: { required: false, showAll: true },
      occurrence: { 
        required: false, 
        defaultValue: 100
      }
    },
    drilldown: { // --- First Level Drilldown ---
      defaultChartType: 'vertical-bar',
      allowedChartTypes: ['vertical-bar', 'horizontal-bar'],
      drilldownLabel: (dataPoint) => dataPoint 
      ? `${dataPoint?.payload?.label}`
      : `${customCaptions.stagesPlural || 'Stages'}`,
      fetchData: async (params) => {
        if (!params.modelId || !params.roleId) {
          return {
            status: false,
            invalidFilters: true,
            message: t("message.pleaseSelectModelAndRole", { model: customCaptions?.model || 'Model', role: customCaptions?.role || 'Role' })
          };
        }
        params['modelVersionRoleId'] = params.roleId;
        
        if (params.occurrence === '' || params.occurrence === undefined) {
          delete params.occurrence;
        }
  
        return await getAverageTimes(params);
      },
      transformData: (response, filterValues) => {
        if (!response?.data?.data) return null;
    
        const transformedData = response.data.data.map(item => ({
          id: item.id,
          label: item.name,
          estimatedMinutes: parseFloat((item.estimatedMinutes).toFixed(2)),
          actualMinutes: parseFloat((item.actualMinutes).toFixed(2)),
          pauseMinutes: parseFloat((item.pauseMinutes || 0).toFixed(2)),
          depth: response.data.depth,
          hasZones: item.hasZones
        }));
        
        return transformedData;
      },
      getParams: (dataPoint, currentParams = {}) => {
        if (!dataPoint) return currentParams;
        
        // First level only handles Version depth
        return {
          ...currentParams,
          modelVersionId: dataPoint.id || ''
        };
      },
      drilldown: { // --- Second Level Drilldown ---
        defaultChartType: 'horizontal-bar',
        allowedChartTypes: ['horizontal-bar', 'vertical-bar'],
        drilldownLabel: (dataPoint, parentDataPoints) => {
          if (dataPoint) {
            return `${dataPoint?.payload?.label}`;
          }
          
          // Check if the parent dataPoint has hasZones=true
          const parentDataPoint = parentDataPoints && parentDataPoints.length > 0 ? parentDataPoints[parentDataPoints.length - 1] : null;
          
          if (parentDataPoint && parentDataPoint.hasZones === false) {
            return `${customCaptions.tasksPlural || 'Tasks'}`;
          }
          
          return `${customCaptions.zonesPlural || 'Zones'}`;
        },
        disableDrilldown: (parentDataPoints) => {
          // Disable further drilldown if the parent has hasZones=false
          const parentDataPoint = parentDataPoints && parentDataPoints.length > 0 ? parentDataPoints[parentDataPoints.length - 1] : null;
          return parentDataPoint && parentDataPoint.hasZones === false;
        },
        fetchData: async (params) => {
          if (!params.modelId || !params.roleId) {
            return {
              status: false,
              invalidFilters: true,
              message: t("message.pleaseSelectModelAndRole", { model: customCaptions?.model || 'Model', role: customCaptions?.role || 'Role' })
            };
          }
          params['modelVersionRoleId'] = params.roleId;
          
          if (params.occurrence === '' || params.occurrence === undefined) {
            delete params.occurrence;
          }
    
          return await getAverageTimes(params);
        },
        getParams: (dataPoint, currentParams = {}) => {
          if (!dataPoint) return currentParams;
          
          // Second level only handles Stage depth
          return {
            ...currentParams,
            modelVersionStageId: dataPoint.id || ''
          };
        },
        drilldown: { // --- Third Level Drilldown ---
          defaultChartType: 'horizontal-bar',
          allowedChartTypes: ['horizontal-bar', 'vertical-bar'],
          drilldownLabel: `${customCaptions.tasksPlural || 'Tasks'}`,
          fetchData: async (params) => {
            if (!params.modelId || !params.roleId) {
              return {
                status: false,
                invalidFilters: true,
                message: t("message.pleaseSelectModelAndRole", { model: customCaptions?.model || 'Model', role: customCaptions?.role || 'Role' })
              };
            }
            params['modelVersionRoleId'] = params.roleId;
            
            if (params.occurrence === '' || params.occurrence === undefined) {
              delete params.occurrence;
            }
      
            return await getAverageTimes(params);
          },
          getParams: (dataPoint, currentParams = {}) => {
            if (!dataPoint) return currentParams;
            
            // Third level only handles Zone depth
            const zoneId = dataPoint.modelVersionZoneId || dataPoint.id || '';
            
            return {
              ...currentParams,
              modelVersionZoneId: zoneId
            };
          }
        }
      }
    }
  };

  return (
    <ReportPage 
      title={t("title.entityAverageTimes", { entity: customCaptions?.service })}
      chartConfig={chartConfig}
    />
  );
};

export default ServiceAverageTimesReport; 
